import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Theme from '../../../../themes/index';
import PropTypes from 'prop-types';
import _, { debounce } from 'lodash';

// Components
import TextInput from '../../../ComponentLibrary/input-elements/oc-text-input';
import EmailInput from '../../../ComponentLibrary/input-elements/oc-email-input';
import PhoneInput from '../../../ComponentLibrary/input-elements/oc-phone-input';
import SelectDropdown from '../../../ComponentLibrary/input-elements/oc-select-dropdown';
import Checkbox from '../../../ComponentLibrary/input-elements/oc-checkbox';
import Error from '../../../ComponentLibrary/input-elements/oc-input-error';
import FormGroup from '../../../ComponentLibrary/forms/oc-form-group';

// Icons
import IconCheck from '../../../ComponentLibrary/icons/icon-check';
import IconExclamationTriangle from '../../../ComponentLibrary/icons/icon-exclamation-triangle';

// Modals
import FormModal from "../../../ComponentLibrary/forms/oc-form-modal";
import ValidatedAddressSuggestionModal from '../modals/ValidatedAddressSuggestionModal';

// Styled Components
const UnvalidatedStreetIcon = styled(IconExclamationTriangle)`
  color: #D65900;
  height: 14px;
  margin-right: 0.5rem;
  width: 16px;
`;

const ValidStreetIcon = styled(IconCheck)`
  color: #3C9748;
  height: 12px;
  margin-right: 0.5rem;
  width: 16px;
`;

const FieldsWrapper = styled.div`
  ${props => props.columns && css`
    display: grid;
    grid-template-columns: 58% 1fr;
    gap: 32px;

    @media screen and (max-width: 767px) {
      gap: 24px;
    }
  `}

  & > :first-of-type {
    margin-bottom: ${props => props.columns ? '0' : '32px'};
  }
`;

const Option = styled.option`
  cursor: pointer;
  font-size: 16px;
  padding: 12px;

  &:hover {
    background: #E6E6E6;
  }
`;

const OptionsContainer = styled.div`
  background-color: #FFFFFF;
  box-shadow: 0px 12px 15px rgb(0 0 0 / 20%);
  position: absolute;
  width: 100%;
  z-index: 10;
`;

const UnvalidatedSmartyAddress = styled.a`
  cursor: pointer;
  display: inline-block;
  text-decoration: none;

  &:hover{
    color: #000;
    text-decoration: underline;
  }
`;

const ValidatedSmartyAddress = styled.p`
  margin: 0;
`;

const CustomLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CustomLabel = styled.span`
  display: inline-block;
  margin-bottom: 16px;
  margin-right: 8px;
  font-size: 16px;
  font-weight: 700;
  position: relative;
`;

const CustomContainer = styled.div`
  display: flex;
  align-items: center;
`;

const stateAbbreviationList = {
  'Arizona': 'AZ',
  'Alabama': 'AL',
  'Alaska': 'AK',
  'Arkansas': 'AR',
  'California': 'CA',
  'Colorado': 'CO',
  'Connecticut': 'CT',
  'Delaware': 'DE',
  'Florida': 'FL',
  'Georgia': 'GA',
  'Hawaii': 'HI',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Indiana': 'IN',
  'Iowa': 'IA',
  'Kansas': 'KS',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Maine': 'ME',
  'Maryland': 'MD',
  'Massachusetts': 'MA',
  'Michigan': 'MI',
  'Minnesota': 'MN',
  'Mississippi': 'MS',
  'Missouri': 'MO',
  'Montana': 'MT',
  'Nebraska': 'NE',
  'Nevada': 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Ohio': 'OH',
  'Oklahoma': 'OK',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Vermont': 'VT',
  'Virginia': 'VA',
  'Washington': 'WA',
  'West Virginia': 'WV',
  'Wisconsin': 'WI',
  'Wyoming': 'WY'
};

const formattedAddress = (jsonAddress) => {
  if (jsonAddress) {
    let formAddress = Object.values(jsonAddress).join(", ")
    return (formAddress)
  } else {
    return ''
  }
}

const formattedAddressValue = (jsonAddress) => {
  if (jsonAddress) {
    let formAddress = Object.values(jsonAddress)
    return (formAddress)
  } else {
    return ({})
  }
}

const fullNameToAbbreviation = (stateFullName) => {
  return stateAbbreviationList[stateFullName]
}

const abbreviationToFullName = (stateAbbreviation) => {
  const stateFullNameList = _.invert(stateAbbreviationList)
  return stateFullNameList[stateAbbreviation]
}

const QuotingWidgetContact = ({
  clientZipCode,
  serviceArea,
  columns,
  states,
  blurValidation,
  changeValidation,
  fields,
  values = {},
  touched = {},
  errors = {},
  submissionError,
  setSubmissionError,
  setSubmissionErrorChildren,
  themeColor = Theme.colors.brand,
  nameRequired = true,
  emailRequired = true,
  phoneRequired = false,
  addressRequired = true,
  consentRequired = true,
  contractorConsentRequest = false,
  contractorConsentLabel = null,
  openFormModal,
  closeFormModal,
  formProps
}) => {
  const stateOptions = states || [
    { text: 'Select', value: "" },
    { text: 'Alabama', value: 'Alabama' },
    { text: 'Alaska', value: 'Alaska' },
    { text: 'Arizona', value: 'Arizona' },
    { text: 'Arkansas', value: 'Arkansas' },
    { text: 'California', value: 'California' },
    { text: 'Colorado', value: 'Colorado' },
    { text: 'Connecticut', value: 'Connecticut' },
    { text: 'Delaware', value: 'Delaware' },
    { text: 'Florida', value: 'Florida' },
    { text: 'Georgia', value: 'Georgia' },
    { text: 'Hawaii', value: 'Hawaii' },
    { text: 'Idaho', value: 'Idaho' },
    { text: 'Illinois', value: 'Illinois' },
    { text: 'Indiana', value: 'Indiana' },
    { text: 'Iowa', value: 'Iowa' },
    { text: 'Kansas', value: 'Kansas' },
    { text: 'Kentucky', value: 'Kentucky' },
    { text: 'Louisiana', value: 'Louisiana' },
    { text: 'Maine', value: 'Maine' },
    { text: 'Maryland', value: 'Maryland' },
    { text: 'Massachusetts', value: 'Massachusetts' },
    { text: 'Michigan', value: 'Michigan' },
    { text: 'Minnesota', value: 'Minnesota' },
    { text: 'Mississippi', value: 'Mississippi' },
    { text: 'Missouri', value: 'Missouri' },
    { text: 'Montana', value: 'Montana' },
    { text: 'Nebraska', value: 'Nebraska' },
    { text: 'Nevada', value: 'Nevada' },
    { text: 'New Hampshire', value: 'New Hampshire' },
    { text: 'New Jersey', value: 'New Jersey' },
    { text: 'New Mexico', value: 'New Mexico' },
    { text: 'New York', value: 'New York' },
    { text: 'North Carolina', value: 'North Carolina' },
    { text: 'North Dakota', value: 'North Dakota' },
    { text: 'Ohio', value: 'Ohio' },
    { text: 'Oklahoma', value: 'Oklahoma' },
    { text: 'Oregon', value: 'Oregon' },
    { text: 'Pennsylvania', value: 'Pennsylvania' },
    { text: 'Rhode Island', value: 'Rhode Island' },
    { text: 'South Carolina', value: 'South Carolina' },
    { text: 'South Dakota', value: 'South Dakota' },
    { text: 'Tennessee', value: 'Tennessee' },
    { text: 'Texas', value: 'Texas' },
    { text: 'Utah', value: 'Utah' },
    { text: 'Vermont', value: 'Vermont' },
    { text: 'Virginia', value: 'Virginia' },
    { text: 'Washington', value: 'Washington' },
    { text: 'West Virginia', value: 'West Virginia' },
    { text: 'Wisconsin', value: 'Wisconsin' },
    { text: 'Wyoming', value: 'Wyoming' }
  ];

  const [validatedAddressValue, setValidatedAddressValue] = useState(false)
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestedValidatedAddress, setSuggestedValidatedAddress] = useState({})
  const [contactAddress, setContactAddress] = useState({})
  const [openAddressModal, setOpenAddressModal] = useState(false)

  //// Address Verification ////
  const changeContactAddress = (field, value) => {
    let newContactAddress = _.cloneDeep(contactAddress)
    newContactAddress[field] = value
    setContactAddress(newContactAddress)
    validateAddress(newContactAddress)
  }

  const changeFullContactAddress = (address) => {
    let state = address.length == 5 ? address[3] : address[2]
    let city = address.length == 5 ? address[2] : address[1]

    let newContactAddress = {
      "address1": address[0],
      "address2": address.length == 5 ? address[1] : '',
      "city": city,
      "state": state.length == 2 ? abbreviationToFullName(state) : state,
      "postal-code": address[address.length - 1]
    }

    setContactAddress(newContactAddress)
    validateAddress(newContactAddress)
  }

  const validateAddress = (contactAddress) => {
    fetch((MDMS_URL + `/api/v1/smarty/verification`), {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        street_1: contactAddress["address1"],
        street_2: contactAddress["address2"] || '',
        city: contactAddress["city"],
        state_province: fullNameToAbbreviation(contactAddress["state"]),
        postal_code: contactAddress["postal-code"]
      })
    }).then(response => {
      if (response.ok) {
        return response.json();
      } else {
        return Promise.reject(response);
      }
    }).then(jsonData => {
      let addressIsSmartyValid = !_.isEmpty(jsonData);
      let contactAddress2 = contactAddress['address2'] || ''
      let jsonDataStreet_2 = jsonData.street_2 || ''

      let suggestedAddress = [jsonData.street_1, jsonDataStreet_2, jsonData.city, abbreviationToFullName(jsonData.state_province), jsonData.postal_code?.split('-')[0]]
      let homeownerAddress = [contactAddress['address1'], contactAddress2, contactAddress['city'], contactAddress['state'], contactAddress['postal-code']]

      if (addressIsSmartyValid) {
        setSuggestedValidatedAddress(suggestedAddress)
      } else {
        setSuggestedValidatedAddress([])
      }

      let addressIsPerfectMatch = (
        suggestedAddress[0] == homeownerAddress[0] &&
        suggestedAddress[1] == homeownerAddress[1] &&
        suggestedAddress[2] == homeownerAddress[2] &&
        suggestedAddress[3] == homeownerAddress[3] &&
        suggestedAddress[4] == homeownerAddress[4]
      )

      if (addressIsSmartyValid && addressIsPerfectMatch) {
        setValidatedAddressValue(true);
      } else {
        setValidatedAddressValue(false);
      }
    }).catch(error => {
      console.log('error', error);
    });
  }

  const validateSuggested = (address) => {
    fetch((MDMS_URL + `/api/v1/smarty/verification`), {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        street_1: address[0],
        street_2: (address.length === 5) ? address[1] : "",
        city: (address.length === 5) ? address[2] : address[1],
        state_province: (address.length === 5) ? abbreviationToFullName(address[3]) : abbreviationToFullName(address[2]),
        postal_code: (address.length === 5) ? address[4] : address[3]
      })
    }).then(
      response => response.json()
    ).then(jsonData => {
      let addressIsSmartyValid = !_.isEmpty(jsonData);
      let jsonDataStreet_2 = jsonData.street_2 || ''

      let suggestedAddress = [jsonData.street_1, jsonDataStreet_2, jsonData.city, abbreviationToFullName(jsonData.state_province), jsonData.postal_code?.split('-')[0]]

      if (addressIsSmartyValid) {
        setSuggestedValidatedAddress(suggestedAddress)
      } else {
        setSuggestedValidatedAddress([])
      }
    }).catch(error => {
      console.log('error', error);
    });
  }

  const debouncedSuggestions = useCallback(
    debounce(value => {
      getAddressSuggestions(value);
      setShowSuggestions(true);
    }, 500), [] // will be created only once initially
  );

  //// Address Suggestions Dropdown ////
  const triggerAddressSuggestions = (event) => {
    let types = ["address1", "address2", "city", "state", "postal-code"]
    let value = event.target.value;
    let newContactAddress = _.cloneDeep(contactAddress);

    newContactAddress["address1"] = value;
    values['address1'] = value;
    types.forEach((type, index) => {
      if (index === 3) {
        let cityName = values[`${type}`];
        let cityAbb = fullNameToAbbreviation(cityName);
        if (!values["address1"].includes(cityName) && !values["address1"].includes(cityAbb)) {
          values[`${type}`] = "";
          newContactAddress[type] = "";
        }
      } else {
        if (!values["address1"].includes(values[`${type}`])) {
          values[`${type}`] = "";
          newContactAddress[type] = "";
        }
      }
    })

    setContactAddress(newContactAddress);
    validateAddress(newContactAddress);
    changeValidation(null, { required: addressRequired }, "address1", value);
    debouncedSuggestions(value);
  }

  const getAddressSuggestions = (addressToSearch) => {
    fetch((MDMS_URL + `/api/v1/smarty/suggestions?street_1=` + addressToSearch), {
      method: 'get',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
    }).then(
      response => response.json()
    ).then(jsonData => {
      let hasSuggestion = !_.isEmpty(jsonData)
      if (hasSuggestion) {
        let suggestions = []
        let initialSuggestions = jsonData.map((address) => {
          Object.keys(address).forEach((k) => address[k] == "" && delete address[k])
          return ({ text: formattedAddress(address), value: formattedAddressValue(address) })
        })

        initialSuggestions.forEach(suggestion => {
          let invalidState = _.isEmpty(abbreviationToFullName(suggestion.value.length == 5 ? suggestion.value[3] : suggestion.value[2]))

          if (!invalidState) {
            suggestions.push(suggestion)
          }
        })

        setAddressSuggestions(suggestions)
      } else {
        setAddressSuggestions([])
      }
    }).catch(error => {
      console.log('error', error);
    });
  }

  const parseAddress = (suggestedAddress) => {
    let address1 = suggestedAddress[0];
    let address2 = suggestedAddress.length == 5 ? suggestedAddress[1] : ""
    let state = suggestedAddress.length == 5 ? suggestedAddress[3] : suggestedAddress[2]
    state = state.length == 2 ? state : fullNameToAbbreviation(state)
    let city = suggestedAddress.length == 5 ? suggestedAddress[2] : suggestedAddress[1]
    let zip = suggestedAddress[suggestedAddress.length - 1]
    return `${address1}${(address2 == "") ? address2 : `, ${address2}, `} ${city}, ${state} ${zip}`;
  }

  const handleAddressChange = (suggestedAddress) => {
    if (typeof suggestedAddress === "string") {
      values["address1"] = suggestedAddress
    } else {
      let state = suggestedAddress.length == 5 ? suggestedAddress[3] : suggestedAddress[2]
      let city = suggestedAddress.length == 5 ? suggestedAddress[2] : suggestedAddress[1]
      let newFullAddress = parseAddress(suggestedAddress);
      values["address1"] = newFullAddress
      values["address2"] = suggestedAddress.length == 5 ? suggestedAddress[1] : ''
      values["city"] = city
      values["state"] = state.length == 2 ? abbreviationToFullName(state) : state
      values["postal-code"] = suggestedAddress[suggestedAddress.length - 1]
      setShowSuggestions(false)
      changeFullContactAddress(suggestedAddress)
    }
  };

  const handleClickListener = (event) => {
    if (event.target.id != 'AddressOption') {
      setShowSuggestions(false)
    }
  }

  //// Address Modal ////
  const openSuggestedAddressModal = () => {
    setOpenAddressModal(true)
  };

  const closeSuggestedAddressModal = () => {
    setOpenAddressModal(false)
  };


  //// useEffects ////
  useEffect(() => {
    if (errors && errors["postal-code"]) {
      let zipModalTrigger = <ModalTrigger
        text="Check your zip code is in our service area."
        modalContent={ZipModalContent}
      />;
      setSubmissionError("Your zip code is not in our service area.");
      setSubmissionErrorChildren(zipModalTrigger);
    } else {
      setSubmissionErrorChildren(null);
    }
  }, [errors]);


  useEffect(() => {
    if (values) {
      values["validated-address"] = validatedAddressValue;
    }
  }, [validatedAddressValue]);

  useEffect(() => {
    if (addressSuggestions.length > 0 && addressSuggestions[0].text !== values["address1"]) {
      validateSuggested(addressSuggestions[0].value);
    }
  }, [addressSuggestions])

  useEffect(() => {
    if (values) {
      let newContactAddress = {
        "address1": values["address1"] || "",
        "address2": values["address2"] || "",
        "city": values["city"] || "",
        "state": (values['state']) || "",
        "postal-code": values["postal-code"] || ""
      }

      setContactAddress(newContactAddress)
      validateAddress(newContactAddress)
    }
  }, []);

  useEffect(() => {
    if (values && !values["state"]) {
      let initialValue = (typeof stateOptions[0] == "string") ? stateOptions[0] : stateOptions[0].value;
      values["state"] = initialValue;
    }
    if (values && !values["city"]) {
      values["city"] = ""
    }
    if (values && !values["postal-code"]) {
      values["postal-code"] = ""
    }
  }, []);

  useEffect(() => {
    document.body.addEventListener('click', handleClickListener, false)
  });

  return (
    <>
      {openAddressModal &&
        <FormModal
          name="Quoting Widget Validated Address Suggestion Modal"
          onClose={() => closeSuggestedAddressModal()}
          open={true}
        >
          <ValidatedAddressSuggestionModal
            typedAddress={values["address1"]}
            validatedSuggestedAddress={suggestedValidatedAddress}
            closeModal={closeSuggestedAddressModal}
            handleAddressChange={handleAddressChange}
          />
        </FormModal>
      }

      <FormGroup multipleFields={true} columns={columns} margin="0">
        <div>
          <TextInput
            label="First Name"
            name="first-name"
            helperText=" "
            labelMargin="0px"
            value={values["first-name"]}
            onBlur={(event) => blurValidation(event, { name: "first-name", required: nameRequired })}
            onChange={(event) => changeValidation(event, { required: nameRequired }, "first-name", event.target.value)}
            required={nameRequired}
            touched={touched["first-name"] || (submissionError && !values["first-name"])}
            valid={!errors?.hasOwnProperty("first-name") && touched["first-name"]}
            error={errors["first-name"]}
            themeColor={themeColor}
            errorIcon={true}
          />
        </div>

        <div>
          <TextInput
            label="Last Name"
            name="last-name"
            helperText=" "
            labelMargin="0px"
            value={values["last-name"]}
            onBlur={(event) => blurValidation(event, { name: "last-name", required: nameRequired })}
            onChange={(event) => changeValidation(event, { required: nameRequired }, "last-name", event.target.value)}
            required={nameRequired}
            touched={touched["last-name"] || (submissionError && !values["last-name"])}
            valid={!errors.hasOwnProperty("last-name") && touched["last-name"]}
            error={errors["last-name"]}
            themeColor={themeColor}
            errorIcon={true}
          />
        </div>
      </FormGroup>

      <FormGroup multipleFields={true} columns={columns} margin="0">
        <div>
          <EmailInput
            label="Email"
            name="email"
            helperText=" "
            value={values["email"]}
            labelMargin="0px"
            onBlur={(event) => blurValidation(event, { name: "email", required: emailRequired })}
            onChange={(event) => changeValidation(event, { required: emailRequired }, "email", event.target.value)}
            required={emailRequired}
            touched={touched["email"] || (submissionError && !values["email"])}
            valid={!errors.hasOwnProperty("email") && touched["email"]}
            error={errors["email"]}
            themeColor={themeColor}
            errorIcon={true}
          />
        </div>

        <div>
          <PhoneInput
            label="Phone"
            name="phone"
            helperText="Optional"
            value={values["phone"]}
            onBlur={(event) => blurValidation(event, { name: "phone", required: phoneRequired })}
            onChange={(event) => changeValidation(event, { required: phoneRequired }, "phone", event.target.value)}
            required={phoneRequired}
            touched={touched["phone"] || (submissionError && !values["phone"])}
            valid={!errors.hasOwnProperty("phone") && touched["phone"]}
            error={errors["phone"]}
            themeColor={themeColor}
          />
        </div>
      </FormGroup>

      <FieldsWrapper>
        <div>
          <CustomLabelWrapper>
            <CustomContainer>
              <CustomLabel>Address</CustomLabel>
            </CustomContainer>
            {values["address1"] &&
            <>
              {validatedAddressValue ?
                <ValidatedSmartyAddress><ValidStreetIcon /> Validated Address</ValidatedSmartyAddress>
                :
                <UnvalidatedSmartyAddress onClick={openSuggestedAddressModal}>
                  <UnvalidatedStreetIcon /> Unvalidated Address
                </UnvalidatedSmartyAddress>
              }
            </>
          }
          </CustomLabelWrapper>
          <TextInput
            label=""
            name="address"
            value={values["address1"]}
            onChange={(event) => triggerAddressSuggestions(event)}
            required={addressRequired}
            touched={touched["address1"] || (submissionError && !values["address1"])}
            valid={!errors?.hasOwnProperty("address1") && touched["address1"]}
            error={errors["address1"]}
            themeColor={themeColor}
            errorIcon={true}
          />
          {addressSuggestions &&
            <OptionsContainer>
              {showSuggestions && addressSuggestions && addressSuggestions.map(suggestion => {
                return (
                  <Option
                    id={'AddressOption'}
                    key={suggestion.value}
                    onClick={() => handleAddressChange(suggestion.value)}
                    value={suggestion.value}
                  >
                    {suggestion.text}
                  </Option>
                );
              })}
            </OptionsContainer>
          }
        </div>
      </FieldsWrapper>

      {consentRequired && <>

        {contractorConsentRequest && contractorConsentLabel &&
          <FormGroup margin="0 0 8px">
            <Checkbox
              name="consent-to-provide-info-to-contractor"
              onChange={(event) => changeValidation(event, _.find(fields, ["name", "consent-to-provide-info-to-contractor"]), "consent-to-provide-info-to-contractor", (event.target.checked ? true : null))}
              required={true}
              value={true}
            >
              <span> {contractorConsentLabel} </span>
            </Checkbox>

            {errors?.hasOwnProperty("consent-to-provide-info-to-contractor") &&
              <Error message={errors["consent-to-provide-info-to-contractor"]} />
            }
          </FormGroup>
        }

        <FormGroup margin="0 0 8px">
          <Checkbox
            name="consent-to-provide-info-to-oc"
            onChange={(event) => changeValidation(event, _.find(fields, ["name", "consent-to-provide-info-to-oc"]), "consent-to-provide-info-to-oc", (event.target.checked ? true : null))}
            required={true}
            value={true}
          >
            <span>By submitting, you provide your information to Owens Corning under their <a href="https://www.owenscorning.com/en-us/terms-of-use?enable=contractor_referral" target="_blank">Terms</a> and <a href="https://www.owenscorning.com/en-us/privacy-policy?enable=contractor_referral" target="_blank">Privacy Policy</a>. </span>
          </Checkbox>

          {errors?.hasOwnProperty("consent-to-provide-info-to-oc") &&
            <Error message={errors["consent-to-provide-info-to-oc"]} />
          }
        </FormGroup>
      </>}
    </>
  )
}

export default QuotingWidgetContact;

QuotingWidgetContact.propTypes = {
  /* CSS: Set to true if you want a 2 column setup */
  columns: PropTypes.bool,
  /* CSS: Hex value for color theme */
  themeColor: PropTypes.string,
  /* HTML: Determines whether or not the address fields are required */
  addressRequired: PropTypes.bool,
  /* HTML: Determines whether or not the email field is required */
  emailRequired: PropTypes.bool,
  /* HTML: Determines whether or not the name fields are required */
  nameRequired: PropTypes.bool,
  /* HTML: Determines whether or not the phone field is required */
  phoneRequired: PropTypes.bool,
  /* JSX: Array of states for the state field */
  states: PropTypes.array
}
